import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { CheckCircle, Loader, ArrowRight, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }
    verifyPayment(sessionId);
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/payments/verify/${sessionId}`,
        { withCredentials: true }
      );
      setPaymentData(data);
      setStatus(data.payment_status === 'paid' ? 'success' : 'pending');
      if (data.payment_status === 'paid') {
        await checkAuth();
      }
    } catch (err) {
      console.error('Verify error:', err);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center p-4">
      <div className="max-w-lg w-full" data-testid="payment-success-page">
        {status === 'verifying' && (
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-12 text-center">
            <Loader size={48} className="animate-spin mx-auto mb-6 text-[var(--secondary)]" />
            <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Verifying Payment...
            </h2>
            <p className="text-[var(--text-secondary)]">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-12 text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h2 className="text-3xl font-black mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Payment Successful!
            </h2>
            <p className="text-lg text-[var(--text-secondary)] mb-2">
              Welcome to <strong>{paymentData?.package_name}</strong>
            </p>
            <div className="bg-[var(--surface-alt)] border-2 border-[var(--border-default)] p-4 my-6">
              <div className="text-3xl font-black">${paymentData?.amount?.toFixed(2)}</div>
              <div className="text-sm text-[var(--text-secondary)]">Payment confirmed</div>
            </div>
            <div className="space-y-3 text-left mb-8">
              <div className="flex items-center gap-3 text-sm">
                <Sparkles size={16} className="text-[var(--secondary)]" />
                <span>Unlimited ideas & AI enhancements</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Sparkles size={16} className="text-[var(--secondary)]" />
                <span>All premium templates</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Sparkles size={16} className="text-[var(--secondary)]" />
                <span>Priority support</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
              className="w-full bg-[var(--obsidian)] text-white font-bold px-6 py-4 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform flex items-center justify-center gap-2"
            >
              Go to Dashboard <ArrowRight size={18} />
            </button>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-12 text-center">
            <Loader size={48} className="animate-spin mx-auto mb-6 text-[var(--warning)]" />
            <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Payment Processing
            </h2>
            <p className="text-[var(--text-secondary)] mb-6">Your payment is still being processed. This may take a moment.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-[var(--secondary)] font-medium hover:underline"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-12 text-center">
            <div className="w-20 h-20 bg-[var(--primary)] rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-white text-3xl font-black">!</span>
            </div>
            <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Something went wrong
            </h2>
            <p className="text-[var(--text-secondary)] mb-6">We couldn't verify your payment. Please contact support.</p>
            <button
              onClick={() => navigate('/pricing')}
              className="bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)]"
            >
              Back to Pricing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
